import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';

import { isAdmin } from '../../auth/admins';
import { useEffectiveUser } from '../../auth/useAuthUser';
import { useMyTabs } from '../../hooks/useAdmin';
import { useDealDetail, useDeals } from '../../hooks/useDeals';
import type {
  DealListItem,
  DealMineTeam,
  DealStatusChip,
} from '../../types';
import {
  readIntentFromUrlSearch,
  resolveEffectiveSelectedDealKey,
} from './DealsPage.deepLink';
import {
  EMBER_NAV_EVENT,
  useReturnTo,
  useSelectionParam,
  useUrlState,
  useViewParam,
} from '../../hooks/navigation/useUrlState';
import {
  CHIPS,
  GROUP_OPTIONS,
  type GroupBy,
  LS_GROUP,
  LS_SORT,
  MINE_TEAM_CROSS_TEAM,
  MINE_TEAM_SELF_ONLY,
  SORT_OPTIONS,
  type SortBy,
  emailToShortName,
} from './DealsPage.shared';
import { AllDoneState } from './DealsPage.AllDoneState';
import { BulkActionToolbar } from './DealsPage.BulkActionToolbar';
import { DealDetail } from './DealsPage.DealDetail';
import { GroupSection } from './DealsPage.DealListRow';
import { UserFilterDropdown } from './DealsPage.UserFilterDropdown';
import { useViewportFill } from '../../hooks/useViewportFill';
import styles from './DealsPage.module.css';

/** Display names for `return_to` targets whose tab key doesn't read as a label. */
const RETURN_TO_LABELS: Record<string, string> = {
  crm: 'CRM',
  pipeline: 'Pipeline',
  // 'pipeline-v1' was dropped 2026-09-01 with the Affinity-fed board. `useReturnTo` validates
  // against TAB_KEYS, so an old link carrying return_to=pipeline-v1 now shows no Back affordance
  // rather than one pointing at a deleted tab.
};

export function DealsPage() {
  const { effectiveEmail } = useEffectiveUser();
  const admin = isAdmin(effectiveEmail);
  const myTabs = useMyTabs();
  const canUseCrossTeamScope =
    admin || myTabs.data?.role === 'admin' || myTabs.data?.role === 'senior';
  // Filters live in the URL (spec §13) so views are shareable + reload-stable.
  // They use `replace`, not push: a deliberate filter change updates the URL in
  // place but is NOT a separate Back-step — Back returns to the originating
  // selection/tab, not through each chip flip.
  const [chip, setChip] = useUrlState<DealStatusChip>('status', {
    parse: (raw) => (raw as DealStatusChip) || 'pending',
    serialize: (v) => (v === 'pending' ? null : v),
    history: 'replace',
  });
  const [mineTeam, setMineTeam] = useUrlState<DealMineTeam>('scope', {
    parse: (raw) => (raw as DealMineTeam) || 'mine',
    serialize: (v) => (v === 'mine' ? null : v),
    history: 'replace',
  });
  const [search, setSearch] = useViewParam('q', '', { history: 'replace' });
  // Selection is a shareable, reload-stable deep link via `?deal_key=<key>`
  // (spec §5.2). The shared hook owns open (push) / close (back-or-replace) /
  // popstate sync. `?key=<opp_id>` legacy links + the one-shot `intent` hint are
  // normalized once on mount below.
  const { value: selectedKey, open: openDeal, close: closeDeal } = useSelectionParam('deal_key');
  const { target: returnTarget, goBack: goBackToPipeline } = useReturnTo();
  // useReturnTo validates against TAB_KEYS; label the affordance for the actual
  // destination (the CRM tab now sends return_to=crm — a "Back to Pipeline" button
  // that lands on CRM would lie). Keys whose display name differs from the key are
  // mapped here; anything else renders its key, which reads acceptably.
  const backLabel = returnTarget
    ? `Back to ${RETURN_TO_LABELS[returnTarget] ?? returnTarget}`
    : 'Back to Pipeline';

  // Group / sort live in the URL too, but seed from localStorage so a user's
  // personal default survives across sessions. URL wins when present;
  // localStorage is written ONLY on an explicit control change (the onChange
  // wrappers below), never on URL-driven init — so opening a shared ?sort= link
  // doesn't clobber the viewer's default (spec §13 / Eng F6.1).
  // Captured ONCE at mount (stable). If it were recomputed each render, an
  // admin toggling "view-as" flips `admin` → the fallback changes → the
  // useUrlState resync (fires on any ember:nav, e.g. a search keystroke) would
  // silently re-seed Group/Sort from the new fallback. Stable fallback ⇒ an
  // absent param always parses to the same value ⇒ no spurious flip (review F1).
  const [groupFallback] = useState<GroupBy>(
    () =>
      (((typeof window !== 'undefined' && localStorage.getItem(LS_GROUP)) ||
        (admin ? 'user' : 'none')) as GroupBy),
  );
  const [groupBy, setGroupRaw] = useUrlState<GroupBy>('group', {
    parse: (raw) => (raw as GroupBy) || groupFallback,
    serialize: (v) => (v === groupFallback ? null : v),
    history: 'replace',
  });
  const setGroupBy = useCallback(
    (v: GroupBy) => {
      setGroupRaw(v);
      if (typeof window !== 'undefined') localStorage.setItem(LS_GROUP, v);
    },
    [setGroupRaw],
  );
  const [sortFallback] = useState<SortBy>(
    () =>
      (((typeof window !== 'undefined' && localStorage.getItem(LS_SORT)) || 'recent') as SortBy),
  );
  const [sortBy, setSortRaw] = useUrlState<SortBy>('sort', {
    parse: (raw) => (raw as SortBy) || sortFallback,
    serialize: (v) => (v === sortFallback ? null : v),
    history: 'replace',
  });
  const setSortBy = useCallback(
    (v: SortBy) => {
      setSortRaw(v);
      if (typeof window !== 'undefined') localStorage.setItem(LS_SORT, v);
    },
    [setSortRaw],
  );
  const [userFilter, setUserFilter] = useState<string[]>([]);
  // Slice J §J2 — bulk-select state. Cleared whenever the filter axes
  // change so users don't act on selections that no longer match the
  // visible list.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected(new Set());
  }, [chip, mineTeam, userFilter, groupBy]);

  // Clamp cross-team scope for non-privileged users. Writes via `replace` (the
  // useUrlState mode for scope) so a shared ?scope=team link for a junior
  // corrects to `mine` without pushing a history entry or fighting the URL
  // (Eng F1.1). Runs as an effect — not parse-time — so it reacts to myTabs
  // loading the role asynchronously.
  useEffect(() => {
    if (myTabs.isLoading) return;
    if (!canUseCrossTeamScope && (mineTeam === 'team' || mineTeam === 'all')) {
      setMineTeam('mine');
    }
  }, [canUseCrossTeamScope, mineTeam, myTabs.isLoading, setMineTeam]);

  // One-shot mount normalization (spec §13): migrate the legacy `?key=<opp_id>`
  // alias to `?deal_key=`; consume + strip the one-shot `intent` hint and apply
  // its posture. `intent=pending_update` forces Pending/Mine (overriding any
  // explicit status/scope on the link); it's stripped so it can't re-fire on
  // reload / MSAL restore / Back, or ride along in a copied link. popstate sync
  // of the selection is owned by useSelectionParam.
  useEffect(() => {
    if (readIntentFromUrlSearch(window.location.search) === 'pending_update') {
      setChip('pending');
      setMineTeam('mine');
    }
    const url = new URL(window.location.href);
    let changed = false;
    const legacy = url.searchParams.get('key');
    if (legacy && !url.searchParams.get('deal_key')) {
      url.searchParams.set('deal_key', legacy);
      url.searchParams.delete('key');
      changed = true;
    }
    if (url.searchParams.has('intent')) {
      url.searchParams.delete('intent');
      changed = true;
    }
    if (changed) {
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      window.dispatchEvent(new CustomEvent(EMBER_NAV_EVENT));
    }
  }, [setChip, setMineTeam]);

  const listQuery = useDeals({
    chip,
    mine_team: mineTeam,
    search: search || undefined,
    user_filter: userFilter.length > 0 ? userFilter : undefined,
  });

  const deals = listQuery.data?.deals ?? [];
  const viewerEmail = listQuery.data?.viewer_email ?? effectiveEmail ?? '';
  const visibleQueueDone = !listQuery.isLoading && deals.length === 0 && chip === 'pending';

  // Catalog of users seen across the current result set — drives the
  // user-filter multi-select chip dropdown options.
  const userCatalog = useMemo(() => {
    const set = new Set<string>();
    for (const d of deals) for (const u of d.visible_to) set.add(u);
    return Array.from(set).sort();
  }, [deals]);

  const grouped = useMemo(
    () => _groupAndSort(deals, groupBy, sortBy, viewerEmail),
    [deals, groupBy, sortBy, viewerEmail],
  );

  const effectiveSelectedKey = useMemo(
    () => resolveEffectiveSelectedDealKey(selectedKey, deals),
    [deals, selectedKey],
  );

  const detailQuery = useDealDetail(effectiveSelectedKey);
  const selectedInVisibleList = useMemo(
    () => !!effectiveSelectedKey && deals.some((d) => d.deal_key === effectiveSelectedKey),
    [deals, effectiveSelectedKey],
  );

  const selectNextPending = useCallback(async (currentKey: string) => {
    const result = await listQuery.refetch();
    const nextDeals = result.data?.deals ?? deals;
    const pendingDeals = nextDeals.filter((d) => d.pending_count > 0);
    if (pendingDeals.length === 0) {
      closeDeal();
      return;
    }
    const currentIndex = pendingDeals.findIndex((d) => d.deal_key === currentKey);
    const next =
      currentIndex >= 0
        ? pendingDeals[currentIndex + 1] ?? pendingDeals[0]
        : pendingDeals[0];
    // Conveyor advance: replace the current selection rather than push, so Back
    // doesn't walk back through already-cleared cards (review F5).
    openDeal(next.deal_key, { replace: true });
  }, [deals, listQuery, openDeal, closeDeal]);

  const mineTeamOptions = canUseCrossTeamScope
    ? MINE_TEAM_CROSS_TEAM
    : MINE_TEAM_SELF_ONLY;

  // Lock the list/detail two-pane body to the remaining viewport height so
  // each pane scrolls internally instead of lengthening the page. Exposed as
  // a CSS var (not an inline height) so the <=960px media query can opt back
  // out to normal stacked flow.
  const { ref: bodyRef, height: bodyHeight } = useViewportFill();

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.controls}>
          <div className={styles.chipBar} role="tablist">
            {CHIPS.map((c) => (
              <button
                key={c.key}
                role="tab"
                aria-selected={chip === c.key}
                className={`${styles.chipBtn} ${chip === c.key ? styles.chipBtnActive : ''}`}
                onClick={() => setChip(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className={styles.chipBar} role="tablist">
            {mineTeamOptions.map((mt) => (
              <button
                key={mt.key}
                role="tab"
                aria-selected={mineTeam === mt.key}
                className={`${styles.chipBtn} ${mineTeam === mt.key ? styles.chipBtnActive : ''}`}
                onClick={() => setMineTeam(mt.key)}
              >
                {mt.label}
              </button>
            ))}
          </div>
          <label className={styles.selectGroup}>
            <span className={styles.selectLabel}>Group</span>
            <select
              className={styles.select}
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            >
              {GROUP_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.selectGroup}>
            <span className={styles.selectLabel}>Sort</span>
            <select
              className={styles.select}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>
          <UserFilterDropdown
            selected={userFilter}
            options={userCatalog}
            onChange={setUserFilter}
          />
          <input
            className={styles.search}
            type="search"
            placeholder="Search name, codename…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {listQuery.isError && (
        <div className={styles.errorBox}>
          Error loading deals:{' '}
          {listQuery.error instanceof Error ? listQuery.error.message : 'unknown'}
        </div>
      )}

      <div
        className={styles.body}
        ref={bodyRef}
        style={
          bodyHeight !== undefined
            ? ({ '--deals-body-height': `${bodyHeight}px` } as CSSProperties)
            : undefined
        }
      >
        <div className={styles.listColumn}>
          {listQuery.isLoading && <div className={styles.loading}>Loading…</div>}
          {visibleQueueDone && (
            <AllDoneState
              canUseCrossTeamScope={canUseCrossTeamScope}
              mineTeam={mineTeam}
              onBackToPipeline={goBackToPipeline}
              backLabel={backLabel}
              onShowTeam={() => setMineTeam('team')}
              onShowAll={() => {
                setChip('all');
                setMineTeam('all');
              }}
            />
          )}
          {!listQuery.isLoading && deals.length === 0 && chip !== 'pending' && (
            <div className={styles.listEmpty}>
              No deals match this filter. Try widening Mine/Team or selecting All.
            </div>
          )}
          {selected.size > 0 && (
            <BulkActionToolbar
              selected={selected}
              deals={deals}
              onClear={() => setSelected(new Set())}
            />
          )}
          {grouped.map((group) => (
            <GroupSection
              key={group.key}
              group={group}
              showHeader={groupBy !== 'none'}
              selectedKey={effectiveSelectedKey}
              viewerEmail={viewerEmail}
              onSelect={(deal_key) => openDeal(deal_key)}
              selectedSet={selected}
              onToggleSelect={(deal_key) =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (next.has(deal_key)) next.delete(deal_key);
                  else next.add(deal_key);
                  return next;
                })
              }
            />
          ))}
        </div>
        <div className={styles.detailColumn}>
          {!effectiveSelectedKey && visibleQueueDone && (
            <AllDoneState
              canUseCrossTeamScope={canUseCrossTeamScope}
              mineTeam={mineTeam}
              onBackToPipeline={goBackToPipeline}
              backLabel={backLabel}
              onShowTeam={() => setMineTeam('team')}
              onShowAll={() => {
                setChip('all');
                setMineTeam('all');
              }}
            />
          )}
          {!effectiveSelectedKey && !visibleQueueDone && (
            <div className={styles.detailPlaceholder}>
              Pick a deal from the list to see its details.
            </div>
          )}
          {effectiveSelectedKey && detailQuery.isLoading && (
            <div className={styles.loading}>Loading deal…</div>
          )}
          {effectiveSelectedKey && detailQuery.data && !selectedInVisibleList && (
            <div className={styles.deepLinkBanner}>
              <span>This update is outside your current filters.</span>
              <button type="button" onClick={() => setMineTeam('mine')} className={styles.btnSecondary}>
                Back to Pending Mine
              </button>
              <button type="button" onClick={goBackToPipeline} className={styles.btnSecondary}>
                {backLabel}
              </button>
              {canUseCrossTeamScope && (
                <>
                  <button type="button" onClick={() => setMineTeam('team')} className={styles.btnSecondary}>
                    Show in Team
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setChip('all');
                      setMineTeam('all');
                    }}
                    className={styles.btnSecondary}
                  >
                    Show All
                  </button>
                </>
              )}
            </div>
          )}
          {detailQuery.data && (
            <DealDetail
              key={detailQuery.data.deal_key}
              payload={detailQuery.data}
              onNextPending={selectNextPending}
              onBackToPipeline={goBackToPipeline}
              backLabel={backLabel}
            />
          )}
          {detailQuery.isError && (
            <div className={styles.errorBox}>Error loading deal details.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

// Slice J §2 — group / sort logic. Group by-user duplicates a deal across
// every user in its visible_to list. All other group-bys are simple
// single-bucket-per-deal assignments.
function _groupAndSort(
  deals: DealListItem[],
  groupBy: GroupBy,
  sortBy: SortBy,
  viewerEmail: string,
): Array<{ key: string; label: string; items: DealListItem[] }> {
  const sortItems = (items: DealListItem[]): DealListItem[] => {
    const out = [...items];
    if (sortBy === 'pending') {
      out.sort((a, b) => b.pending_count - a.pending_count);
    } else if (sortBy === 'oldest') {
      out.sort((a, b) => (a.last_event_at ?? '').localeCompare(b.last_event_at ?? ''));
    } else {
      out.sort((a, b) => (b.last_event_at ?? '').localeCompare(a.last_event_at ?? ''));
    }
    return out;
  };

  if (groupBy === 'none') {
    return [{ key: 'all', label: 'All deals', items: sortItems(deals) }];
  }

  const buckets = new Map<string, { label: string; items: DealListItem[] }>();
  const upsert = (key: string, label: string, item: DealListItem) => {
    const existing = buckets.get(key);
    if (existing) existing.items.push(item);
    else buckets.set(key, { label, items: [item] });
  };

  for (const d of deals) {
    if (groupBy === 'badge') {
      const key = d.badge;
      const label = d.badge === 'NEW_DEAL' ? 'New Deals' : 'Updates';
      upsert(key, label, d);
    } else if (groupBy === 'stage') {
      const key = d.stage_abbrev ?? '(no stage)';
      upsert(key, key, d);
    } else if (groupBy === 'deal_lead') {
      const key = d.deal_lead ?? '(no lead)';
      upsert(key, key, d);
    } else if (groupBy === 'last_event') {
      const { key, label } = _bucketByLastEvent(d.last_event_at);
      upsert(key, label, d);
    } else if (groupBy === 'user') {
      // Duplicate the deal under EACH user in visible_to. Intentional —
      // matches the spec's group-by-user expectation that a multi-user deal
      // appears under every user's section.
      const users = d.visible_to.length > 0 ? d.visible_to : ['(unattributed)'];
      for (const u of users) {
        upsert(u, emailToShortName(u), d);
      }
    }
  }

  // Order groups: viewer first when grouping by user, otherwise by label.
  const entries = Array.from(buckets.entries());
  entries.sort(([a, av], [b, bv]) => {
    if (groupBy === 'user') {
      if (a === viewerEmail) return -1;
      if (b === viewerEmail) return 1;
    }
    return av.label.localeCompare(bv.label);
  });
  return entries.map(([key, v]) => ({
    key,
    label: v.label,
    items: sortItems(v.items),
  }));
}

function _bucketByLastEvent(iso: string | null): { key: string; label: string } {
  if (!iso) return { key: 'z:none', label: 'No recent event' };
  const date = new Date(iso);
  if (isNaN(date.getTime())) return { key: 'z:none', label: 'No recent event' };
  const now = new Date();
  const day = 24 * 60 * 60 * 1000;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.floor((today.getTime() - eventDay.getTime()) / day);
  if (diff <= 0) return { key: 'a:today', label: 'Today' };
  if (diff === 1) return { key: 'b:yesterday', label: 'Yesterday' };
  if (diff <= 7) return { key: 'c:this_week', label: 'This week' };
  if (diff <= 14) return { key: 'd:last_week', label: 'Last week' };
  return { key: 'e:older', label: 'Older' };
}
